import React, { useState, useEffect } from 'react';
import { Button, TextField, Typography, Box, Link, InputAdornment, IconButton, Paper, Avatar } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login, getCurrentUser } from '../services/authService';
import { getFirstAccessibleRoute, hasRouteAccess } from '../utils/navigationHelper';
import { setPermissions, setPermissionsLoading } from '../app/store/slices/authSlice';
import { getUserPermissions } from '../features/access-management/services/accessManagementService';
import { useAuth } from '../contexts/AuthContext';
import Loader from './Loader';
import LanguageSwitcher from './LanguageSwitcher';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { modulePermissions, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const checkUser = async () => {
      const user = getCurrentUser();
      if (user && !justLoggedIn) {
        if (authLoading) return;
        try {
          const firstRoute = await getFirstAccessibleRoute(user.id || user.email, user.role, modulePermissions);
          navigate(firstRoute, { replace: true });
        } catch (error) {
          toast.error(t('login_access_error'));
        }
      }
    };
    checkUser();
  }, [navigate, modulePermissions, authLoading, justLoggedIn, t]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      toast.error(t('login_required_fields'));
      return;
    }
    setLoading(true);
    setJustLoggedIn(true);
    try {
      const { user } = await login(email, password);
      const uid = user?.id || user?.email;
      const userRole = user?.role || user?.Role || 'User';
      let targetRoute = location.state?.from?.pathname;
      if (!targetRoute || targetRoute === '/login') targetRoute = null;
      if (userRole === 'SuperAdmin' || userRole === 'CompanyAdmin') {
        navigate(targetRoute || '/dashboard', { replace: true });
        return;
      }
      let perms = modulePermissions;
      if (!perms || Object.keys(perms).length === 0) {
        dispatch(setPermissionsLoading(true));
        try {
          const response = await getUserPermissions(uid);
          perms = response?.permissions || response?.payload || {};
          dispatch(setPermissions(perms));
        } catch (permissionError) {
          perms = {};
          dispatch(setPermissions({}));
        } finally {
          dispatch(setPermissionsLoading(false));
        }
      }
      const firstRoute = await getFirstAccessibleRoute(uid, userRole, perms);
      const destination = targetRoute && hasRouteAccess(perms, targetRoute) ? targetRoute : firstRoute;
      navigate(destination, { replace: true });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', boxSizing: 'border-box', display: 'flex', justifyContent: 'center', background: '#f5f7fb', overflow: 'hidden', fontFamily: '"Trebuchet MS", "Segoe UI", sans-serif', '@supports (height: 100dvh)': { minHeight: '100dvh' } }}>
      <Box sx={{ position: 'fixed', top: { xs: 7, sm: 9 }, right: { xs: 7, sm: 9 }, zIndex: 10 }}>
        <LanguageSwitcher />
      </Box>
      <Box sx={{ width: '100%', maxWidth: 1180, minHeight: { xs: '100vh', md: 'auto' }, boxSizing: 'border-box', mx: 'auto', display: 'grid', alignItems: { xs: 'stretch', md: 'center' }, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.95fr) minmax(0, 1.05fr)' }, p: { xs: 0, md: 2.5 }, gap: { xs: 0, md: 2.5 }, '@supports (height: 100dvh)': { minHeight: { xs: '100dvh', md: 'auto' } } }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, width: '100%', minWidth: 0, height: { md: 'clamp(480px, calc(100dvh - 40px), 680px)' }, boxSizing: 'border-box', borderRadius: 5, p: { md: 5, lg: 7 }, flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', textAlign: 'center', color: '#173b63', background: 'linear-gradient(145deg, #c5dcf4 0%, #dbe9f7 58%, #cfe2f4 100%)', boxShadow: '0 24px 60px rgba(55, 92, 130, 0.15)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Box component="img" src="/assets/logo.png" alt="Logo" sx={{ width: 52, height: 52, objectFit: 'contain', borderRadius: 2.5, backgroundColor: '#fff', p: 1, boxShadow: '0 8px 18px rgba(37, 99, 235, 0.12)' }} />
            <Typography sx={{ mt: 3, fontWeight: 700, fontSize: { md: '1.7rem', lg: '2rem' }, letterSpacing: '0.04em' }}>Billeur</Typography>
          </Box>
          <Box sx={{ maxWidth: 410, mx: 'auto' }}>
            <Typography sx={{ color: '#1d5fba', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', mb: 2 }}>{t('login_eyebrow')}</Typography>
            <Typography component="h2" sx={{ fontSize: { md: '1.9rem', lg: '2.35rem' }, lineHeight: 1.08, fontWeight: 700, mb: 2 }}>{t('login_title')}</Typography>
            <Typography sx={{ color: '#46647f', lineHeight: 1.7 }}>{t('login_description')}</Typography>
          </Box>
          <Typography sx={{ color: '#5f7b96', fontSize: '0.8rem' }}>{t('login_footer_tagline')}</Typography>
        </Box>
        <Paper elevation={0} sx={{ width: '100%', minWidth: 0, height: { md: 'clamp(480px, calc(100dvh - 40px), 680px)' }, minHeight: { xs: '100vh', md: 'auto' }, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 5, md: 6, lg: 8 }, borderRadius: { xs: 0, md: 5 }, background: '#fff', boxShadow: { xs: 'none', md: '0 24px 60px rgba(16, 42, 70, 0.08)' } }}>
          <Box sx={{ width: '100%', maxWidth: 390 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', alignItems: 'center', gap: 1, mb: 6 }}>
              <Box component="img" src="/assets/logo.png" alt="Logo" sx={{ width: 42, height: 42, objectFit: 'contain', borderRadius: 2, p: 0.75, boxShadow: '0 5px 14px rgba(16, 42, 70, 0.12)' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#173b63', letterSpacing: '0.04em' }}>Billeur</Typography>
            </Box>
            <Typography component="h1" sx={{ fontWeight: 700, color: '#173b63', fontSize: { xs: '2rem', sm: '2.35rem' }, lineHeight: 1.1, mb: 1 }}>{t('login_welcome')}</Typography>
            <Typography variant="body2" sx={{ mb: 4, color: '#6b7d91' }}>{t('login_subtitle')}</Typography>
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%', '& .MuiTextField-root': { mb: 2 }, '& .MuiOutlinedInput-root': { borderRadius: 2.5, backgroundColor: '#fbfcfe', '& fieldset': { borderColor: '#dbe4ee' }, '&:hover fieldset': { borderColor: '#8eadd2' }, '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1 } } }}>
              <TextField label={t('login_email')} type="email" fullWidth margin="none" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={loading} />
              <TextField label={t('login_password')} type={showPassword ? 'text' : 'password'} fullWidth margin="none" value={password} onChange={(event) => setPassword(event.target.value)} required disabled={loading} InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton aria-label={t('login_toggle_password')} onClick={() => setShowPassword(!showPassword)} onMouseDown={(event) => event.preventDefault()} edge="end" disabled={loading} sx={{ color: '#2563eb' }}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} />
              <Box sx={{ textAlign: 'right', mb: 3 }}><Link component={RouterLink} to="/forgot-password" sx={{ fontSize: '0.875rem', color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>{t('login_forgot_password')}</Link></Box>
              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ py: 1.45, borderRadius: 2.5, fontSize: '0.98rem', fontWeight: 600, background: '#2563eb', '&:hover': { background: '#1d4ed8', transform: 'translateY(-2px)' }, '&:disabled': { background: '#94a3b8' } }}>{loading ? t('login_signing_in') : t('login_sign_in')}</Button>
            </Box>
            <Typography variant="body2" sx={{ mt: 4, textAlign: 'center', color: '#9aa8b7', fontSize: '0.78rem' }}>{t('login_copyright', { year: new Date().getFullYear() })}</Typography>
          </Box>
        </Paper>
      </Box>
      <Loader open={loading} />
    </Box>
  );
};

export default Login;
